// src/components/GuideCard/GuideCard.js
import React, { useState, useCallback, useEffect } from 'react';
import GuideDrawer from './GuideDrawer';
import '../../styles/components/GuideCard.css';

// ── Thumbnail placeholder (no screenshot) ───────────────────────────────────
const ThumbnailPlaceholder = ({ name }) => (
  <div className="guide-tile__placeholder" aria-hidden="true">
    <span className="guide-tile__placeholder-bar" />
    <span className="guide-tile__placeholder-bar guide-tile__placeholder-bar--short" />
    <span className="guide-tile__placeholder-target" />
    <span className="guide-tile__placeholder-letter">{name?.charAt(0)?.toUpperCase() || 'G'}</span>
  </div>
);

// ── Badge ────────────────────────────────────────────────────────────────────
const Badge = ({ guide }) => {
  if (guide.is_public) return <span className="m-badge m-badge--public">Public</span>;
  if (guide.shared_emails?.length > 0) return <span className="m-badge m-badge--shared">Shared</span>;
  return <span className="m-badge m-badge--private">Private</span>;
};

// ── Main card tile ───────────────────────────────────────────────────────────
const GuideCard = ({
  guide,
  cardIndex        = 0,
  showDelete       = false,
  onDelete,
  showDownload     = false,
  onDownload,
  onDownloadPrivacy,
  userPlan,
  isOwner          = false,
  onShare,
  onEdit,
  blurPreview      = false,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [imgError,   setImgError]   = useState(false);
  const [copied,     setCopied]     = useState(false);
  // Stamp reads "03 steps" on arrival, then tucks to "03" (hover reopens it).
  const [stampTucked, setStampTucked] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStampTucked(true), 1400 + cardIndex * 60);
    return () => clearTimeout(t);
  }, [cardIndex]);

  const stepCount = guide.steps?.length ?? 0;
  const stamp = guide.updated_at || guide.created_at;
  const dateLabel = stamp
    ? new Date(stamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;
  const hasThumbnail = guide.thumbnail_path && !imgError;

  const handleShortcutClick = useCallback((e) => {
    e.stopPropagation(); // prevent the card's onClick from firing
    if (!guide.shortcut) return;
    navigator.clipboard.writeText(guide.shortcut).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }).catch(() => {
      // Fallback for browsers that block clipboard without focus
      const ta = document.createElement('textarea');
      ta.value = guide.shortcut;
      ta.style.cssText = 'position:fixed;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [guide.shortcut]);

  return (
    <>
      <article
        className="guide-tile"
        style={{ '--card-index': cardIndex }}
        onClick={() => setDrawerOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setDrawerOpen(true)}
        aria-label={`Open ${guide.name}`}
      >
        {/* ── Thumbnail (inset, like a photo clipped to an index card) ── */}
        <div className="guide-tile__thumb">
          {hasThumbnail ? (
            <img
              src={guide.thumbnail_path}
              alt={`${guide.name} preview`}
              className={`guide-tile__thumb-img${blurPreview ? ' privacy-blur' : ''}`}
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <ThumbnailPlaceholder name={guide.name} />
          )}
          <span className={`guide-tile__count${stampTucked ? ' is-tucked' : ''}`}>
            {String(stepCount).padStart(2, '0')}
            <span className="guide-tile__count-label">&nbsp;{stepCount === 1 ? 'step' : 'steps'}</span>
          </span>
        </div>

        <div className="guide-tile__body">
          <h3 className="guide-tile__title">{guide.name}</h3>
          {guide.owner_name && (
            <div className="guide-tile__owner">by {guide.owner_name}</div>
          )}
          {guide.description && (
            <p className="guide-tile__desc">{guide.description}</p>
          )}
        </div>

        {/* ── Footer: status, shortcut, date ─────────────────────────── */}
        <div className="guide-tile__meta">
          <Badge guide={guide} />
          {dateLabel && <span className="guide-tile__date">{dateLabel}</span>}
          {guide.shortcut && (
            <span
              className={`guide-tile__shortcut${copied ? ' guide-tile__shortcut--copied' : ''}`}
              onClick={handleShortcutClick}
              role="button"
              tabIndex={0}
              title={copied ? 'Copied!' : 'Click to copy shortcut'}
              aria-label={copied ? 'Shortcut copied' : `Copy shortcut ${guide.shortcut}`}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleShortcutClick(e); }}}
            >
              {copied ? 'Copied!' : guide.shortcut}
            </span>
          )}
        </div>
      </article>

      {/* ── Slide-over drawer ──────────────────────────────────────────── */}
      {drawerOpen && (
        <GuideDrawer
          guide={guide}
          isOwner={isOwner}
          showDelete={showDelete}
          showDownload={showDownload}
          onDelete={onDelete}
          onDownload={onDownload}
          onDownloadPrivacy={onDownloadPrivacy}
          userPlan={userPlan}
          onShare={onShare}
          onEdit={onEdit}
          blurPreview={blurPreview}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
};

export default GuideCard;
